import React, { Suspense, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { ResponsivePie } from "@nivo/pie"
import { theme as themeConfig } from "antd"

import ErrorBoundary from "../../../../../../components/ErrorBoundary"
import SpinnerLoader from "../../../../../../components/SpinnerLoader"

const GRAPH_COLORS = [
  "#5B8FF9",
  "#61DDAA",
  "#65789B",
  "#F6BD16",
  "#7262FD",
  "#78D3F8",
  "#9661BC",
  "#F6903D",
  "#E86452",
  "#36CFC9",
]

const PieChartWithLegend = ({
  graph,
  chartColors,
}) => {
  const { token } = themeConfig.useToken()
  const [legendData, setLegendData] = useState([])
  const [filteredGraphData, setFilteredGraphData] = useState([])

  useEffect(() => {
    if (!graph?.graphData || graph?.graphData.length === 0) return
    const graphData = graph?.graphData
    const updatedLegendData = []
    const validData = []

    graphData.forEach((element, index) => {
      // Prefer id > label > fallback label
      const label = element.label || element.id || `Label ${index + 1}`
      const value = element.value ?? 0
      const percentage = element.percentage

      let legendLabel = `${label}: ${value}`
      if (percentage !== undefined) {
        legendLabel = `${label}: ${value} (${percentage}%)`
      }
      updatedLegendData.push({
        id: label,
        label: legendLabel,
        color: chartColors[index % chartColors.length],
      })

      if (value > 0) {
        validData.push({
          id: label,
          label,
          value,
        })
      }
    })

    setLegendData(updatedLegendData)
    setFilteredGraphData(validData)
  }, [graph, chartColors])

  return (
    <Suspense fallback={<SpinnerLoader />}>
      <ErrorBoundary>
          <div
            className="ori-flex-row"
            style={{
              height: "350px",
              position: "relative",
              paddingTop: token.paddingXS,
              paddingBottom: token.paddingXS,
            }}
          >
            <ResponsivePie
              data={filteredGraphData}
              sortByValue
              colors={chartColors.length > 0 ? chartColors : { scheme: "nivo" }}
              margin={{ top: 40, right: 200, bottom: 40, left: 40 }} // Increased right margin from 120 to 200
              innerRadius={0.5}
              padAngle={0.7}
              activeOuterRadiusOffset={8}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
              arcLabel={({ value }) => `${value}`}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [] }}
              theme={{
                textColor: token.colorText,
                tooltip: {
                  container: {
                    background: token.colorBgContainer,
                    color: token.colorText,
                  },
                },
              }}
              legends={[
                {
                  anchor: "top-right",
                  direction: "column",
                  justify: false,
                  translateX: 120, 
                  translateY: -30,   
                  itemWidth: 140,  
                  itemHeight: 18,  
                  itemsSpacing: 8, 
                  symbolSize: 18,  
                  itemDirection: "left-to-right",
                  itemTextColor: token.colorText,
                  data: legendData,
                },
              ]}
            />
          </div>
      </ErrorBoundary>
    </Suspense>
  )
}

const mapStateToProps = state => ({
  loading: state.generativeai_details.loading,
  loadError: state.generativeai_details.loadError,
})

PieChartWithLegend.propTypes = {
  graphData: PropTypes.array,
  loading: PropTypes.bool,
  loadError: PropTypes.bool,
  title: PropTypes.string,
  chartColors: PropTypes.array,
  handleRetry: PropTypes.func,
}

PieChartWithLegend.defaultProps = {
  graphData: [],
  loading: false,
  loadError: false,
  title: "",
  chartColors: process.env.REACT_APP_GRAPH_COLOR
    ? process.env.REACT_APP_GRAPH_COLOR.split(",")
    : GRAPH_COLORS,
}

export default connect(mapStateToProps)(PieChartWithLegend)